import { useCallback } from 'react'

import { scope } from '../../hoc/withPermissions'
import { useAuth } from '../useAuth/useAuth'
import { useMoneyFormat } from '../useMoneyFormat/useMoneyFormat'
import { usePermission } from '../usePermission/usePermission'
import { useQuery } from '../useQuery/useQuery'

import { resolveRewardMoney } from './resolveRewardMoney'
import { getGamificationPartnerConfig } from '@percent/workplace-giving/api/gamification/getGamificationPartnerConfig/getGamificationPartnerConfig'
import {
  getAccountConfigFromAuthState,
  getPartnerFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'

export type GivingActionRewardValue = Readonly<{
  isLoading: boolean
  isRewardConfigured: boolean
  formatReward: (points: number | null | undefined, rewardsEligible: boolean) => string | undefined
}>

export const useGivingActionRewardValue = (): GivingActionRewardValue => {
  const has = usePermission()
  const { state: authState } = useAuth()
  const { moneyFormat } = useMoneyFormat()
  const hasGamificationScope = has(scope('gamification'))

  const { data, isLoading } = useQuery(['getGamificationPartnerConfig'], getGamificationPartnerConfig, {
    staleTime: 1000 * 60,
    enabled: hasGamificationScope
  })

  const currency =
    getAccountConfigFromAuthState(authState)?.currency ?? getPartnerFromAuthState(authState)?.currencyCode

  const formatReward = useCallback(
    (points: number | null | undefined, rewardsEligible: boolean) => {
      const reward = rewardsEligible ? resolveRewardMoney(data ?? undefined, currency ?? undefined, points) : undefined

      return reward ? moneyFormat(reward, { hideDecimalsIfZero: true }) : undefined
    },
    [data, currency, moneyFormat]
  )

  return {
    isLoading,
    isRewardConfigured: !!data,
    formatReward
  }
}
